import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import SpeedIcon from '@mui/icons-material/Speed';
import MDBox from '../components/MDBox';
import MDTypography from '../components/MDTypography';
import { useTheme } from '@mui/material/styles';
import GaugeChart from 'react-gauge-chart';
import { usePeriod } from '../components/PeriodContext';
import InfoIcon from '@mui/icons-material/Info';
import Tooltip from '@mui/material/Tooltip';
import { CircularProgress } from '@mui/material';
import { db, collection, query, getDocs, where } from '../firebaseConfig';

Date.prototype.getWeek = function () {
  const date = new Date(this.getTime());
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 4 - (date.getDay() || 7));
  const yearStart = new Date(date.getFullYear(), 0, 1);
  const weekNumber = Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
  return weekNumber;
};

const TeamProfitFactorGauge = ({ userID, tradeAccountID }) => {
  const { selectedPeriod, selectedYear, selectedMonth, showMissedTrades, selectedTag } = usePeriod();
  const [journalEntries, setJournalEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasData, setHasData] = useState(false);
  const theme = useTheme();
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];

  useEffect(() => {
    const fetchTrades = async () => {
      try {
        setLoading(true);
        const tradesPath = `Users/${userID}/tradeAccounts/${tradeAccountID}/trades`;
        const tradesQuery = query(
          collection(db, tradesPath),
          where('status', '==', showMissedTrades ? 'Missed' : 'Closed')
        );
        const tradesSnapshot = await getDocs(tradesQuery);
        const tradesData = tradesSnapshot.docs.map((doc) => ({
          id: doc.id,
          date: doc.data().date ? new Date(doc.data().date) : new Date(),
          ...doc.data(),
        }));
        console.log('TeamProfitFactorGauge: Fetched trades for userID:', userID, 'tradeAccountID:', tradeAccountID, tradesData);
        setJournalEntries(tradesData);
        setHasData(tradesData.length > 0);
      } catch (error) {
        console.error('TeamProfitFactorGauge: Error fetching trades:', error);
        setJournalEntries([]);
        setHasData(false);
      } finally {
        setLoading(false);
      }
    };

    if (userID && tradeAccountID) {
      fetchTrades();
    } else {
      setJournalEntries([]);
      setHasData(false);
      setLoading(false);
    }
  }, [userID, tradeAccountID, showMissedTrades]);

  const filterEntries = (entries) => {
    return entries.filter((entry) => {
      const entryDate = new Date(entry.date);
      const matchesPeriod = () => {
        if (selectedPeriod === 'today') {
          return entryDate.toDateString() === new Date().toDateString();
        } else if (selectedPeriod === 'this-week') {
          return (
            entryDate.getWeek() === new Date().getWeek() &&
            entryDate.getFullYear() === new Date().getFullYear()
          );
        } else if (selectedPeriod === 'this-month') {
          return (
            entryDate.getMonth() === new Date().getMonth() &&
            entryDate.getFullYear() === new Date().getFullYear()
          );
        } else if (selectedPeriod === 'this-year') {
          return entryDate.getFullYear() === new Date().getFullYear();
        } else if (selectedPeriod === 'pick-month') {
          return entryDate.getMonth() + 1 === selectedMonth && entryDate.getFullYear() === selectedYear;
        } else if (selectedPeriod === 'pick-year') {
          return entryDate.getFullYear() === selectedYear;
        } else if (selectedPeriod === 'pick-month-year') {
          return entryDate.getMonth() + 1 === selectedMonth && entryDate.getFullYear() === selectedYear;
        } else {
          return true; // all-time
        }
      };

      const matchesTag =
        selectedTag === 'all' || (Array.isArray(entry.tags) && entry.tags.includes(selectedTag));
      return matchesPeriod() && matchesTag;
    });
  };

  const calculateProfitFactor = () => {
    let totalWins = 0;
    let totalLosses = 0;

    filterEntries(journalEntries).forEach((entry) => {
      const returns = parseFloat(entry.returns);
      if (returns > 0) {
        totalWins += returns;
      } else if (returns < 0) {
        totalLosses += Math.abs(returns);
      }
    });

    if (totalLosses === 0 && totalWins === 0) {
      return 0; // No trades
    } else if (totalLosses === 0) {
      return 5; // All trades are wins, cap at upper limit
    }

    const rawProfitFactor = totalWins / totalLosses;
    return Math.min(rawProfitFactor, 5); // Cap at 5 for display
  };

  const profitFactor = useMemo(
    () => calculateProfitFactor(),
    [journalEntries, selectedPeriod, selectedYear, selectedMonth, showMissedTrades, selectedTag]
  );

  const upperLimit = 5;
  const gaugeValue = Math.min(profitFactor / upperLimit, 1);
  const profitFactorColor = profitFactor >= 1 ? 'success' : 'error';

  const tooltipContent = `Profit Factor measures trading effectiveness for ${
    showMissedTrades ? 'missed' : 'closed'
  } trades.\n\nCalculated as total profits divided by total losses.\n\nBelow 1: Losing strategy.\n1-2: Decent performance.\n2-3: Good performance.\n3+: Excellent performance.\n\nCapped at 5 for display.`;

  const renderContent = () => {
    if (loading) {
      return (
        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </MDBox>
      );
    }

    if (!hasData) {
      return (
        <MDBox
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
          textAlign="center"
          px={2}
        >
          <InfoIcon sx={{ fontSize: 40, mb: 2, color: iconColor }} />
          <MDTypography variant="h6" color="text">
            No trading data available yet
          </MDTypography>
          <MDTypography variant="body2" color="text">
            Complete some trades to see your performance charts
          </MDTypography>
        </MDBox>
      );
    }

    return (
      <MDBox pb={2} px={2} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
        <GaugeChart
          id="profit-factor-gauge"
          nrOfLevels={20}
          colors={['#FF0000', '#FFFF00', '#00FF00']}
          arcWidth={0.3}
          percent={gaugeValue}
          hideText={true}
          needleColor="#345243"
          formatTextValue={() => ''}
          style={{ height: '110px', width: '100%', maxWidth: '250px' }}
          animate={false}
        />
        <MDBox display="flex" justifyContent="space-between" width="100%" flexWrap="wrap">
          <MDTypography component="span" variant="button" fontWeight="bold" fontFamily="inherit" color="error">
            Poor
          </MDTypography>
          <MDTypography component="span" variant="button" fontWeight="bold" fontFamily="inherit" color="success">
            Excellent
          </MDTypography>
        </MDBox>
        <MDTypography
          component="span"
          variant="button"
          fontWeight="bold"
          fontFamily="inherit"
          color={profitFactorColor}
        >
          {profitFactor.toFixed(2)}
        </MDTypography>
      </MDBox>
    );
  };

  return (
    <Card sx={{ minHeight: '300px', padding: '16px' }}>
      <MDBox display="flex" flexDirection="column" justifyContent="space-between" pt={1} px={2} height="100%">
        <MDBox display="flex" justifyContent="space-between" alignItems="center">
          <MDBox
            variant="gradient"
            bgColor="light"
            color="dark"
            coloredShadow="light"
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="4rem"
            height="4rem"
            mt={-5}
          >
            <SpeedIcon fontSize="medium" color="inherit" />
          </MDBox>
          <MDBox textAlign="right" lineHeight={1.25}>
            <MDTypography variant="button" fontWeight="light" color="text" fontFamily="inherit">
              Profit Factor {showMissedTrades ? '(Missed)' : ''}
            </MDTypography>
            <Tooltip
              title={tooltipContent}
              placement="bottom-end"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    border: `1px solid ${theme.palette.divider}`,
                    p: 1.5,
                    maxWidth: '250px',
                    fontSize: '0.75rem',
                    lineHeight: 1.5,
                    '& .MuiTooltip-arrow': {
                      color: theme.palette.background.paper,
                    },
                  },
                },
              }}
            >
              <InfoIcon
                sx={{
                  ml: 1,
                  fontSize: '1rem',
                  color: iconColor,
                  cursor: 'help',
                }}
              />
            </Tooltip>
            <MDTypography variant="h4" fontFamily="inherit" style={{ visibility: 'hidden' }}>
              0
            </MDTypography>
          </MDBox>
        </MDBox>
        <Divider />
        {renderContent()}
      </MDBox>
    </Card>
  );
};

TeamProfitFactorGauge.propTypes = {
  userID: PropTypes.string.isRequired,
  tradeAccountID: PropTypes.string.isRequired,
};

export default TeamProfitFactorGauge;